import type { SubscriptionState, HeldPlanPrice, BillingCatalog } from './useBillingSummary';
import { formatDay, formatWhen } from '../../lib/time';

export const PLAN_LABELS: Record<SubscriptionState['plan'], string> = {
  free: 'Free',
  personal: 'Personal',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
};

/**
 * What the plan held bills, as Stripe has it, or nothing: the catalogue is
 * what a new purchase costs, and a customer on an older price must not be
 * shown it as their renewal (Codex review round 1).
 */
export function heldPrice(price: HeldPlanPrice | null | undefined, perSeat: boolean): string {
  if (!price) return '';
  const amount =
    price.currency === 'usd'
      ? dollars(price.unitAmountCents)
      : otherCurrency(price.unitAmountCents, price.currency);
  return ` · ${amount}${perSeat ? ' / seat' : ''} / ${price.interval === 'annual' ? 'yr' : 'mo'}`;
}

/** Stripe's zero-decimal and three-decimal currencies; every other one is in hundredths. */
const STRIPE_ZERO_DECIMAL = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);
const STRIPE_THREE_DECIMAL = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd']);

/**
 * Stripe sends amounts in its own minor unit for each currency: cents, whole
 * yen for JPY (z.ai review round 1), and hundredths for ISK and UGX even
 * though they are shown without decimals (Codex review round 3). Converted by
 * Stripe's rule, then formatted by Intl.
 */
export function stripeAmount(amount: number, currency: string): number {
  const code = currency.toLowerCase();
  const exponent = STRIPE_ZERO_DECIMAL.has(code) ? 0 : STRIPE_THREE_DECIMAL.has(code) ? 3 : 2;
  return amount / 10 ** exponent;
}

function otherCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(stripeAmount(amount, currency));
  } catch {
    return `${stripeAmount(amount, currency)} ${currency.toUpperCase()}`;
  }
}

/** "$12", "$1,290", "$12.50". */
export function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

type PriceLabels = Record<'personal' | 'team' | 'business', { monthly: string; annual: string }>;

/**
 * The price on each plan button, from the server's catalogue. Without one (a
 * page loaded ahead of its API during a deploy) the buttons still work and
 * say which interval they buy.
 */
export function priceLabels(catalog: BillingCatalog | undefined): PriceLabels {
  const label = (plan: 'personal' | 'team' | 'business') => {
    const price = catalog?.plans[plan];
    if (!price) return { monthly: 'Monthly', annual: 'Annual' };
    const seat = price.perSeat ? ' / seat' : '';
    return {
      monthly: `${dollars(price.monthlyCents)}${seat} / mo`,
      annual: `${dollars(price.annualCents)}${seat} / yr${price.annualSaving ? ` (${price.annualSaving})` : ''}`,
    };
  };
  return { personal: label('personal'), team: label('team'), business: label('business') };
}

/** A billing date (a renewal, a period's end) as the day it falls on in the person's zone (TOADO-904). */
export function formatBillingDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Unavailable' : formatDay(date);
}

/**
 * When a meter resets, in the person's own time (TOADO-904). Meters reset at
 * midnight UTC, the evening before for anyone west of London, so the day
 * alone ("November 1") was wrong for them: "Oct 31, 8:00 PM your time".
 */
export function formatResetTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Unavailable' : `${formatWhen(date, { withTime: true })} your time`;
}
